import { useState } from "react"
import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table"
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { Search, Edit, FileKey, ChevronLeft, ChevronRight, Loader2, Plus } from "lucide-react"

export function PolicyList() {
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  const { data, isLoading, isError, refetch } = useApiQuery("getPolicies", {})

  const policies = ((data as any)?.content?.policies || (data as any)?.policies || []).map((p: any) => ({
    id: p.id || String(p.policyNumber),
    policyNumber: p.policyNumber,
    policyUri: p.policyUri,
    role: p.role,
    version: p.version,
    allow: p.allow ?? [],
    deny: p.deny ?? [],
    resourceInstances: p.resourceInstances ?? [],
    conditions: p.conditions ?? {},
  }))

  const filteredPolicies = policies.filter((p: any) =>
    p.policyUri?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.role?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const totalPages = Math.ceil(filteredPolicies.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedPolicies = filteredPolicies.slice(startIndex, startIndex + itemsPerPage)

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-destructive">Failed to load policies</p>
          <Button variant="outline" onClick={() => refetch()}>Try Again</Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <FileKey className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Manage Policies</CardTitle>
                <CardDescription className="text-base">Define RBAC access policies for roles and resources</CardDescription>
              </div>
            </div>
            <Link to="/rema/policies/add">
              <Button size="sm" className="gap-2">
                <Plus className="h-4 w-4" />
                Add Policy
              </Button>
            </Link>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search policies..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1) }}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <Table className="text-base">
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead className="w-[80px] font-semibold text-base">ID</TableHead>
                <TableHead className="font-semibold text-base">URI</TableHead>
                <TableHead className="font-semibold text-base">Role</TableHead>
                <TableHead className="w-[100px] font-semibold text-base">Version</TableHead>
                <TableHead className="w-[90px] text-right font-semibold text-base">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedPolicies.length > 0 ? (
                paginatedPolicies.map((policy: any) => (
                  <TableRow key={policy.policyNumber} className="transition-colors hover:bg-muted/50">
                    <TableCell className="font-medium text-base">{policy.policyNumber}</TableCell>
                    <TableCell className="max-w-[260px]">
                      <Link
                        to="/rema/policies/$policyNumber"
                        params={{ policyNumber: String(policy.policyNumber) }}
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        <span className="text-sm font-mono truncate block" title={policy.policyUri}>
                          {policy.policyUri}
                        </span>
                      </Link>
                    </TableCell>
                    <TableCell className="text-base text-muted-foreground max-w-[200px]">
                      <span className="truncate block" title={policy.role}>{policy.role || '—'}</span>
                    </TableCell>
                    <TableCell className="text-base">{policy.version || '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                        <Link to="/rema/policies/$policyNumber/edit" params={{ policyNumber: String(policy.policyNumber) }}>
                          <Edit className="h-4 w-4" />
                          <span className="sr-only">Edit policy {policy.policyNumber}</span>
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                    No policies found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t py-3">
              <p className="text-base text-muted-foreground">
                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredPolicies.length)} of {filteredPolicies.length} policies
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium">Page {currentPage} of {totalPages}</span>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
